/**
 * Velociraptor integration types
 *
 * Types for interacting with Velociraptor server for artefact listing,
 * collector building, and offline collection packages.
 */

// =============================================================================
// ENUM TYPES
// =============================================================================

/**
 * Status of Velociraptor connection
 */
export type VelociraptorConnectionStatus = 'connected' | 'disconnected' | 'error' | 'unknown'

/**
 * Build status for collector packages
 */
export type CollectorBuildStatus = 'pending' | 'building' | 'completed' | 'failed'

/**
 * Artefact parameter type
 */
export type ArtefactParameterType = 'string' | 'int' | 'bool' | 'choices' | 'timestamp'

// =============================================================================
// ENTITY TYPES
// =============================================================================

/**
 * Velociraptor health/status response
 */
export interface VelociraptorStatus {
  connected: boolean
  serverVersion: string | null
  serverUrl: string | null
  lastChecked: string
  error: string | null
}

/**
 * VQL artefact definition
 */
export interface VqlArtefact {
  name: string
  description: string | null
  author: string | null
  category: string | null
  parameters: VqlArtefactParameter[]
  sources: string[]
  tools: string[]
  precondition: string | null
  isBuiltIn: boolean
}

/**
 * VQL artefact parameter
 */
export interface VqlArtefactParameter {
  name: string
  description: string | null
  paramType: string
  defaultValue: string | null
  choices: string[] | null
  required: boolean
}

/**
 * Artefact configuration for collector
 */
export interface ArtefactConfig {
  name: string
  parameters: Record<string, string | number | boolean>
}

/**
 * Collector build configuration
 */
export interface CollectorBuildConfig {
  targetOs: string
  artefacts: ArtefactConfig[]
  outputFormat: string
  encryptionPassword: string | null
  compressionLevel: number
  cpuLimit: number | null
  iopsLimit: number | null
  progressTimeout: number | null
  timeout: number | null
}

/**
 * Built collector package
 */
export interface BuiltPackage {
  buildId: string
  status: string
  filename: string | null
  fileSizeBytes: number | null
  sha256Hash: string | null
  downloadUrl: string | null
  buildStartedAt: string
  buildCompletedAt: string | null
  error: string | null
}

/**
 * Collector build result
 */
export interface CollectorBuildResult {
  buildId: string
  status: string
  message: string | null
}

// =============================================================================
// INPUT TYPES
// =============================================================================

/**
 * Input for building a collector
 */
export interface BuildCollectorInput {
  targetOs: string
  artefacts: ArtefactConfig[]
  outputFormat?: string
  encryptionPassword?: string
  compressionLevel?: number
  cpuLimit?: number
  iopsLimit?: number
  progressTimeout?: number
  timeout?: number
}

/**
 * Input for building collector from template
 */
export interface BuildFromTemplateInput {
  collectionRequestId: string
  targetOs: string
  encryptionPassword?: string
}

// =============================================================================
// QUERY PARAMS
// =============================================================================

/**
 * Query parameters for listing artefacts
 */
export interface ListArtefactsParams {
  category?: string
  search?: string
  builtInOnly?: boolean
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/**
 * Artefacts list response
 */
export interface ArtefactsListResponse {
  artefacts: VqlArtefact[]
  total: number
}

// =============================================================================
// DISPLAY CONSTANTS
// =============================================================================

/**
 * Labels for connection status
 */
export const CONNECTION_STATUS_LABELS: Record<VelociraptorConnectionStatus, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  error: 'Error',
  unknown: 'Unknown',
}

/**
 * Labels for build status
 */
export const BUILD_STATUS_LABELS: Record<CollectorBuildStatus, string> = {
  pending: 'Pending',
  building: 'Building',
  completed: 'Completed',
  failed: 'Failed',
}

/**
 * Labels for artefact parameter types
 */
export const PARAMETER_TYPE_LABELS: Record<ArtefactParameterType, string> = {
  string: 'Text',
  int: 'Number',
  bool: 'Yes/No',
  choices: 'Selection',
  timestamp: 'Date/Time',
}

/**
 * Colours for connection status
 */
export const CONNECTION_STATUS_COLOURS: Record<VelociraptorConnectionStatus, string> = {
  connected: 'green',
  disconnected: 'gray',
  error: 'red',
  unknown: 'yellow',
}

/**
 * Colours for build status
 */
export const BUILD_STATUS_COLOURS: Record<CollectorBuildStatus, string> = {
  pending: 'yellow',
  building: 'blue',
  completed: 'green',
  failed: 'red',
}

/**
 * Common artefact categories
 */
export const ARTEFACT_CATEGORIES = [
  'Windows.System',
  'Windows.Forensics',
  'Windows.EventLogs',
  'Windows.Registry',
  'Windows.Persistence',
  'Windows.Network',
  'Linux.System',
  'Linux.Forensics',
  'MacOS.System',
  'MacOS.Forensics',
  'Generic.Forensic',
  'Server',
  'Custom',
] as const

/**
 * Target OS options
 */
export const TARGET_OS_OPTIONS = [
  { value: 'windows_x86', label: 'Windows (32-bit)' },
  { value: 'windows', label: 'Windows (64-bit)' },
  { value: 'linux', label: 'Linux' },
  { value: 'darwin', label: 'macOS (Intel)' },
  { value: 'darwin_arm64', label: 'macOS (Apple Silicon)' },
] as const

/**
 * Output format options
 */
export const OUTPUT_FORMAT_OPTIONS = [
  { value: 'zip', label: 'ZIP Archive' },
  { value: 'tar.gz', label: 'Tar GZip' },
] as const
